import type { FilterSelection } from '@prerender/design-system';
import type { CrawlerPickerChange } from '@prerender/design-system/CrawlerPicker';

import {
  ACTIVE_CRAWLER_TYPES,
  type ActiveCrawlerType,
  type CrawlerCategoryScheme,
  crawlerTypeCategories,
  currentCategoryKey,
  isActiveCrawlerType,
  isRetiredCategory,
  successorOfRetiredCategory,
} from './Crawlers';

export type CrawlerFilter =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'some'; types: ActiveCrawlerType[]; categories: string[] };

export type QueryableCrawlerFilter = Exclude<CrawlerFilter, { kind: 'none' }>;

export type CrawlerSelectedEvent = {
  selected_crawlers: string[];
  crawler_selection: CrawlerFilter['kind'];
};

export type CrawlerPickedEvent = CrawlerSelectedEvent & {
  interaction: CrawlerPickerChange['kind'];
  target?: string;
  checked: boolean;
};

// Both keys are always present so that writing a filter also clears the
// parameter it no longer uses.
export type CrawlerFilterSearchParams = {
  crawlerTypes: string | undefined;
  crawlers: string | undefined;
};

export type StoredCrawlerFilter = {
  crawlerTypes: string[] | undefined;
  crawlers: string[];
};

const CRAWLER_TYPES_PARAM = 'crawlerTypes';
const CRAWLERS_PARAM = 'crawlers';
const ALL_CRAWLERS_EVENT_VALUE = 'All crawlers';

export const ALL_CRAWLERS: { kind: 'all' } = { kind: 'all' };
export const NO_CRAWLERS: { kind: 'none' } = { kind: 'none' };

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function cleanKeys(keys: string[]): string[] {
  return keys.map((key) => key.trim()).filter((key) => key.length > 0);
}

function splitKeys(value: string | null | undefined): string[] {
  return cleanKeys((value ?? '').split(','));
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? cleanKeys(value.filter((item): item is string => typeof item === 'string')) : [];
}

function joinKeys(keys: string[]): string | undefined {
  return keys.length > 0 ? keys.join(',') : undefined;
}

function buildFilter(
  typeKeys: string[],
  categoryKeys: string[],
  scheme: CrawlerCategoryScheme
): QueryableCrawlerFilter {
  const types = unique(typeKeys.filter(isActiveCrawlerType));
  const categoriesCoveredByTypes = new Set(types.flatMap((type) => crawlerTypeCategories(type, scheme)));
  const requestedCategories = unique(categoryKeys.map(currentCategoryKey));
  const categories = requestedCategories.filter(
    (category) => !isRetiredCategory(category, scheme) && !categoriesCoveredByTypes.has(category)
  );

  if (types.length > 0 || categories.length > 0) return { kind: 'some', types, categories };

  const successors = successorsOfRetiredCategories(requestedCategories, scheme);
  if (successors.length > 0) return { kind: 'some', types: [], categories: successors };
  return ALL_CRAWLERS;
}

function successorsOfRetiredCategories(categories: string[], scheme: CrawlerCategoryScheme): string[] {
  return unique(
    categories.flatMap((category) => {
      const successor = successorOfRetiredCategory(category, scheme);
      return successor === undefined ? [] : [successor];
    })
  );
}

export function fromCategories(
  categories: string[],
  scheme: CrawlerCategoryScheme
): QueryableCrawlerFilter {
  return buildFilter([], cleanKeys(categories), scheme);
}

export function toCategories(
  filter: QueryableCrawlerFilter,
  scheme: CrawlerCategoryScheme
): string[] {
  if (filter.kind === 'all') return [];
  return unique([...filter.types.flatMap((type) => crawlerTypeCategories(type, scheme)), ...filter.categories]);
}

export function collapseToAll(
  filter: QueryableCrawlerFilter,
  available: string[],
  scheme: CrawlerCategoryScheme
): QueryableCrawlerFilter;
export function collapseToAll(
  filter: CrawlerFilter,
  available: string[],
  scheme: CrawlerCategoryScheme
): CrawlerFilter;
export function collapseToAll(
  filter: CrawlerFilter,
  available: string[],
  scheme: CrawlerCategoryScheme
): CrawlerFilter {
  if (filter.kind !== 'some') return filter;
  return coversEveryCategory(toCategories(filter, scheme), available) ? ALL_CRAWLERS : filter;
}

function coversEveryCategory(selected: string[], available: string[]): boolean {
  const selectedCategories = new Set(selected);
  return available.length > 0 && available.every((category) => selectedCategories.has(category));
}

export function fromSearchParams(
  params: URLSearchParams,
  scheme: CrawlerCategoryScheme
): QueryableCrawlerFilter {
  return buildFilter(
    splitKeys(lastValue(params, CRAWLER_TYPES_PARAM)),
    splitKeys(lastValue(params, CRAWLERS_PARAM)),
    scheme
  );
}

// The pages read every other parameter through Object.fromEntries, which keeps the last of repeated keys.
function lastValue(params: URLSearchParams, name: string): string | undefined {
  const values = params.getAll(name);
  return values[values.length - 1];
}

export function toSearchParams(filter: CrawlerFilter): CrawlerFilterSearchParams {
  if (filter.kind !== 'some') return { crawlerTypes: undefined, crawlers: undefined };
  return { crawlerTypes: joinKeys(filter.types), crawlers: joinKeys(filter.categories) };
}

export function fromStored(
  value: unknown,
  scheme: CrawlerCategoryScheme
): QueryableCrawlerFilter {
  if (typeof value !== 'object' || value === null) return ALL_CRAWLERS;

  const stored = value as Partial<Record<keyof StoredCrawlerFilter, unknown>>;
  return buildFilter(stringList(stored.crawlerTypes), stringList(stored.crawlers), scheme);
}

export function toStored(filter: CrawlerFilter): StoredCrawlerFilter {
  if (filter.kind !== 'some') return { crawlerTypes: undefined, crawlers: [] };
  return { crawlerTypes: filter.types.length > 0 ? filter.types : undefined, crawlers: filter.categories };
}

export function fromPicker(
  selected: FilterSelection,
  available: string[],
  scheme: CrawlerCategoryScheme
): CrawlerFilter {
  if (selected === null) return NO_CRAWLERS;

  const categories = cleanKeys(selected);
  const types = ACTIVE_CRAWLER_TYPES.filter((type) =>
    coversEveryCategory(categories, availableCategoriesOf(type, available, scheme))
  );
  return collapseToAll(buildFilter(types, categories, scheme), available, scheme);
}

function availableCategoriesOf(type: ActiveCrawlerType, available: string[], scheme: CrawlerCategoryScheme): string[] {
  const availableCategories = new Set(available);
  return crawlerTypeCategories(type, scheme).filter((category) => availableCategories.has(category));
}

export function toPicker(
  filter: CrawlerFilter,
  available: string[],
  scheme: CrawlerCategoryScheme
): string[] | null {
  if (filter.kind === 'all') return [];
  if (filter.kind === 'none') return null;

  const availableCategories = new Set(available);
  const selected = toCategories(filter, scheme).filter((category) => availableCategories.has(category));
  return selected.length > 0 ? selected : null;
}

export function toCrawlerSelectedEvent(
  filter: CrawlerFilter,
  available: string[],
  scheme: CrawlerCategoryScheme
): CrawlerSelectedEvent {
  const effective = collapseToAll(filter, available, scheme);
  return { selected_crawlers: selectedCrawlersOf(effective, scheme), crawler_selection: effective.kind };
}

function selectedCrawlersOf(filter: CrawlerFilter, scheme: CrawlerCategoryScheme): string[] {
  if (filter.kind === 'all') return [ALL_CRAWLERS_EVENT_VALUE];
  if (filter.kind === 'none') return [];
  return toCategories(filter, scheme);
}

export function toCrawlerPickedEvent(
  selected: FilterSelection,
  available: string[],
  change: CrawlerPickerChange,
  scheme: CrawlerCategoryScheme
): CrawlerPickedEvent {
  return {
    ...toCrawlerSelectedEvent(pickedFilter(selected, available, scheme), available, scheme),
    interaction: change.kind,
    ...(change.kind === 'all' ? {} : { target: change.id }),
    checked: change.checked,
  };
}

function pickedFilter(selected: FilterSelection, available: string[], scheme: CrawlerCategoryScheme): CrawlerFilter {
  return selected === null ? NO_CRAWLERS : collapseToAll(fromCategories(selected, scheme), available, scheme);
}
